import type { ReactNode } from 'react';
import { cn } from './cn';

export type StatTone = 'brand' | 'gold' | 'sky' | 'success' | 'warning' | 'danger' | 'neutral';

const ACCENT: Record<StatTone, { border: string; chip: string }> = {
  brand: { border: 'border-l-brand-500', chip: 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300' },
  gold: { border: 'border-l-harvest-400', chip: 'bg-harvest-50 text-harvest-700 dark:bg-harvest-400/15 dark:text-harvest-300' },
  sky: { border: 'border-l-sky-500', chip: 'bg-sky-500/10 text-sky-600 dark:text-sky-400' },
  success: { border: 'border-l-brand-500', chip: 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300' },
  warning: { border: 'border-l-warning-500', chip: 'bg-warning-500/12 text-warning-600 dark:text-harvest-300' },
  danger: { border: 'border-l-danger-500', chip: 'bg-danger-500/10 text-danger-600 dark:text-danger-500' },
  neutral: { border: 'border-l-line-strong', chip: 'bg-surface-2 text-muted' },
};

export interface StatCardProps {
  label: ReactNode;
  /** The value. Pass a `<CountUp>` from `ui/motion` here when you want it to animate. */
  value: ReactNode;
  /** Optional secondary line under the value (e.g. "vs last month", a sublabel). */
  hint?: ReactNode;
  /** A delta chip, e.g. "+12%". `direction` colours it. */
  delta?: { label: ReactNode; direction?: 'up' | 'down' | 'flat' };
  icon?: ReactNode;
  /** Accent colour for the left border + icon chip. */
  tone?: StatTone;
  /** Makes the whole card a keyboard-accessible button (for click-to-filter/drill). */
  onClick?: () => void;
  className?: string;
}

/**
 * One KPI card for the whole app — the audit found 5+ different KPI treatments
 * (inline-hex `Kpi`, antd `Statistic`, bespoke `kpi()`/`KpiTile`) with broken dark
 * mode and low-contrast value text. This renders values in `text-content` (legible),
 * keeps the accent to the border/icon only, flips correctly in dark mode, and is
 * keyboard-accessible when clickable. Count-up lives in the value slot (`ui/motion`),
 * so this file stays free of the GSAP dependency.
 */
export function StatCard({ label, value, hint, delta, icon, tone = 'brand', onClick, className }: StatCardProps) {
  const a = ACCENT[tone];
  const clickable = !!onClick;
  const deltaCls =
    delta?.direction === 'up'
      ? 'bg-brand-500/12 text-brand-700 dark:text-brand-300'
      : delta?.direction === 'down'
        ? 'bg-danger-500/10 text-danger-600 dark:text-danger-500'
        : 'bg-surface-3 text-muted';

  const Comp: 'button' | 'div' = clickable ? 'button' : 'div';
  return (
    <Comp
      {...(clickable ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'flex items-start gap-3 rounded-card border border-l-4 border-line bg-surface p-4 text-left shadow-soft',
        a.border,
        clickable &&
          'cursor-pointer transition duration-150 hover:-translate-y-0.5 hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
        className,
      )}
    >
      {icon && (
        <span className={cn('grid size-10 shrink-0 place-items-center rounded-control [&>svg]:size-5', a.chip)}>
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-muted">{label}</div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-2xl font-bold tracking-tight text-content">{value}</span>
          {delta && (
            <span className={cn('rounded-pill px-1.5 py-px text-[11px] font-semibold', deltaCls)}>
              {delta.label}
            </span>
          )}
        </div>
        {hint && <div className="mt-1 truncate text-xs text-subtle">{hint}</div>}
      </div>
    </Comp>
  );
}
